import { NextSeo, NextSeoProps } from 'next-seo'
import { useTheme } from 'next-themes'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import AuthModal from '~/src/components/AuthModal'
import ErrorToast from '~/src/components/ErrorToast'
import Footer from '~/src/components/Footer'
import LayoutHeader from '~/src/components/LayoutHeader'
import { Toaster } from '~/src/components/ui/sonner'
import { useAppContext } from '~/src/context/AppProvider'
import { cn } from '~/src/lib/utils'
import tailwindConfig from '~/tailwind.config'

const LayoutAppsHellobar = dynamic(
  () => import('~/src/components/LayoutAppsHellobar'),
  {
    ssr: false,
  }
)

// const AmazonPrimeHellobar = dynamic(
//   () => import('~/src/components/AmazonPrimeHellobar'),
//   {
//     ssr: false,
//   }
// )

const SmartBanner = dynamic(() => import('~/src/components/SmartBanner'), {
  ssr: false,
})

interface Props extends React.HTMLProps<HTMLDivElement> {
  children?: React.ReactNode
  nextSeo?: NextSeoProps
  hideHeader?: boolean
  hideFooter?: boolean
  bottomReached?: boolean
  hideBorders?: boolean
  LogoComponent?: React.ReactNode
  showSmartBanner?: boolean
  showExtensionHellobar?: boolean
  fullWidth?: boolean
  minHeight?: boolean
  centerContent?: boolean
  hideTryIt?: boolean
  hidePoints?: boolean
  stickyHeader?: boolean
  clearBgHeader?: boolean
  backgroundTheme?: string
  mainClassName?: string
  headerClassName?: string
  inputClassName?: string
  darkMode?: boolean
  themeOverride?: 'light' | 'dark'
}

const Layout: React.FC<Props> = ({
  id,
  children,
  className,
  nextSeo,
  hideHeader,
  hideFooter,
  bottomReached,
  hideBorders,
  LogoComponent,
  showSmartBanner,
  showExtensionHellobar,
  fullWidth,
  minHeight,
  centerContent,
  hideTryIt,
  hidePoints,
  stickyHeader,
  clearBgHeader,
  backgroundTheme,
  mainClassName,
  headerClassName,
  inputClassName,
  darkMode,
  themeOverride,
}) => {
  const { errorMessage, setErrorMessage } = useAppContext()
  const { resolvedTheme } = useTheme()
  const isDarkMode = resolvedTheme === 'dark' || darkMode

  // State to hold theme color
  const [themeColor, setThemeColor] = useState<string | null>('#ffffff')

  // Add useEffect to update theme color only on client side
  useEffect(() => {
    const color =
      backgroundTheme ||
      (isDarkMode ? tailwindConfig.theme.extend.colors.darkest : '#ffffff')

    setThemeColor(color)
  }, [backgroundTheme, isDarkMode])

  // This useEffect fix the issue of hydration:
  // A React hydration issue occurs when the initial UI rendered on the client-side
  // does not precisely match the HTML content that was generated and sent from
  // the server during Server-Side Rendering (SSR).
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  const router = useRouter()
  const isHomePage = router.pathname === '/'

  return (
    <>
      <NextSeo
        // TODO : ensure we pass in dupe.com and the current path as the canonical url.
        {...nextSeo}
      />
      {showSmartBanner && <SmartBanner />}
      {/* Only render meta tag on client after theme is determined */}
      {themeColor && <meta name='theme-color' content={themeColor} />}
      <div
        className={cn('', {
          dark: isDarkMode && mounted,
        })}
      >
        <main
          id={id}
          data-vaul-drawer-wrapper
          className={cn(
            'dark:bg-darkest flex min-h-[100dvh] flex-col bg-white text-black dark:text-white/90',
            mainClassName,
            // For pages other than the the home page add a margin left to account for the sidebar width
            // Should only apply on desktop (md:)
            {
              'md:ml-[var(--sidebar-width-collapsed)]': !isHomePage,
            }
          )}
        >
          {!!showExtensionHellobar && <LayoutAppsHellobar />}
          {!hideHeader && (
            <LayoutHeader
              hideTryIt={hideTryIt}
              sticky={stickyHeader}
              expand={!!bottomReached}
              hidePoints={hidePoints}
              LogoComponent={LogoComponent}
              className={headerClassName}
              inputClassName={inputClassName}
              theme={themeOverride}
              clearBg={clearBgHeader}
            />
          )}

          <div
            className={cn(
              'dark:bg-darkest mx-auto w-full flex-grow bg-white',
              {
                'min-h-[100dvh]': minHeight,
                'flex flex-col items-center justify-center': centerContent,
                'max-w-screen-sm': !fullWidth,
                'border-black md:border-l-2 md:border-r-2': !hideBorders,
              },
              className
            )}
          >
            {children}

            {errorMessage && (
              <ErrorToast
                errorMessage={errorMessage}
                setErrMsg={setErrorMessage}
              />
            )}
          </div>
          {!hideFooter && <Footer />}
          <AuthModal />
          <Toaster
            position='top-center'
            richColors
            // className='!z-[999]'
          />
        </main>
      </div>
    </>
  )
}

export default React.memo(Layout)
